/**
 * The codes the camera sees, in the order to try them: the first is the read,
 * the rest are the other codes in view.
 *
 * A read passes every code in view. The code the store accepted last goes
 * last, since the store would ignore it; a code that just came into view goes
 * before one that was already there, because it is what the operator pointed
 * at; and the biggest on screen goes first — a box often carries a retail EAN
 * and an internal Code 128 side by side.
 *
 * @param {Array<{rawValue: string, boundingBox?: {width: number, height: number}}>} codes
 * @param {object} [options]
 * @param {Set<string>} [options.seen] - The raw values that were already in view.
 * @param {string|null} [options.skip] - The last code the store accepted.
 * @returns {string[]} Trimmed, each once; empty when nothing is readable.
 */
export const orderReadCodes = (codes, { seen = new Set(), skip = null } = {}) => {
  const area = (code) => (code.boundingBox?.width || 0) * (code.boundingBox?.height || 0);
  const rank = (code) => {
    if (code.rawValue.trim() === skip) return 2;
    return seen.has(code.rawValue.trim()) ? 1 : 0;
  };
  const ordered = (codes || [])
    .filter((code) => String(code?.rawValue ?? '').trim())
    .sort((a, b) => rank(a) - rank(b) || area(b) - area(a));
  return [...new Set(ordered.map((code) => code.rawValue.trim()))];
};

/**
 * What a run of the camera remembers between looks: each code's stay in view
 * (`stays`) and every code seen in the run.
 *
 * A stay is `{since, lastSeen, again, used, read}`: when the code came into
 * view, when it was last seen, whether it had been seen before in the run
 * when it came, whether a read the screen took used it up — as the read or as
 * a code beside it — and whether it was that read.
 */
export const emptyViewMemory = () => ({ stays: new Map(), seenInRun: new Set() });

const valuesInView = (codes) => [
  ...new Set((codes || []).map((code) => String(code?.rawValue ?? '').trim()).filter(Boolean)),
];

/**
 * One look at the codes in view, and the read it offers, if any. A code is
 * offered from when it comes into view until a read the screen takes uses it
 * up (`tookRead`): a read the screen does not take — busy, or showing the last
 * verdict — is offered again at the next look. It comes into view again only
 * after `gap` ms out of it, as the next unit of the same product held up to
 * the camera, and then `again` says it was already seen in this run. A run
 * starts with the camera, so a label left in view across a restart — a sheet
 * that closed — is read, but never `again`.
 *
 * @param {Array<{rawValue: string, boundingBox?: {width: number, height: number}}>} codes - Every code in view.
 * @param {object} memory - The previous look's (`emptyViewMemory`).
 * @param {{now: number, gap: number, skip?: string|null}} options - `skip`: the last code the store accepted.
 * @returns {{read: {code: string, alternatives: string[], again: boolean}|null, memory: object}}
 */
export const readFromView = (codes, memory, { now, gap, skip = null }) => {
  const inView = valuesInView(codes);
  const stays = new Map(memory.stays);
  inView.forEach((value) => {
    const stay = stays.get(value);
    stays.set(
      value,
      stay && now - stay.lastSeen < gap
        ? { ...stay, lastSeen: now }
        : { since: now, lastSeen: now, again: memory.seenInRun.has(value), used: false, read: false }
    );
  });

  const arrivedBefore = new Set(inView.filter((value) => stays.get(value).since < now));
  const ordered = orderReadCodes(codes, { seen: arrivedBefore, skip });
  const code = ordered.find((value) => !stays.get(value).used);
  const read = code
    ? { code, alternatives: ordered.filter((value) => value !== code), again: stays.get(code).again }
    : null;
  return { read, memory: { stays, seenInRun: new Set([...memory.seenInRun, ...inView]) } };
};

/**
 * The screen took the read: it and the codes beside it are used up for as long
 * as they stay in view.
 *
 * @param {object} memory
 * @param {{code: string, alternatives: string[]}} read
 */
export const tookRead = (memory, read) => {
  const stays = new Map(memory.stays);
  [read.code, ...read.alternatives].forEach((value) => {
    const stay = stays.get(value);
    if (stay) stays.set(value, { ...stay, used: true, read: stay.read || value === read.code });
  });
  return { ...memory, stays };
};

/**
 * The store rejected a read: the camera starts over on what is in front of it,
 * so the same label, or the one beside it, is read again once the screen
 * takes reads. A code still in view reads as new, never `again`: a product
 * counted before the rejection, still in view, does not count twice. One out
 * of view keeps having been seen: coming back is still `again`.
 *
 * @param {object} memory
 * @param {{now: number, gap: number}} options
 */
export const forgetView = (memory, { now, gap }) => {
  const inView = new Set([...memory.stays].filter(([, stay]) => now - stay.lastSeen < gap).map(([value]) => value));
  return { stays: new Map(), seenInRun: new Set([...memory.seenInRun].filter((value) => !inView.has(value))) };
};

/**
 * The screen moved on to its next step: every code the run remembers is
 * offered once more, but `skip`, the code it just took. The product seen
 * beside the slot just verified is the next read.
 *
 * @param {object} memory
 * @param {string|null} [skip]
 */
export const offerAgain = (memory, skip = null) => {
  const stays = new Map(memory.stays);
  stays.forEach((stay, value) => {
    if (value !== skip) stays.set(value, { ...stay, used: false, read: false });
  });
  return { ...memory, stays };
};

/**
 * How the camera is, from a check of its video track every `HEALTH_EVERY_MS`.
 * The library sees none of these, and its loop can stop with them: `slept`
 * when the checks themselves stopped for a while, `ended` when the track is
 * over — another app took the camera —, `muted` when it sent no image on two
 * checks in a row.
 *
 * @param {{sinceLastCheck: number, track: {readyState: string, muted: boolean}|null, mutedChecks?: number}} check
 * @param {{sleptAfter: number}} options
 * @returns {{stall: 'slept'|'ended'|'muted'|null, mutedChecks: number}}
 */
export const cameraHealth = ({ sinceLastCheck, track, mutedChecks = 0 }, { sleptAfter }) => {
  if (sinceLastCheck > sleptAfter) return { stall: 'slept', mutedChecks: 0 };
  if (!track) return { stall: null, mutedChecks: 0 };
  if (track.readyState === 'ended') return { stall: 'ended', mutedChecks: 0 };
  if (!track.muted) return { stall: null, mutedChecks: 0 };
  return mutedChecks + 1 >= 2 ? { stall: 'muted', mutedChecks: 0 } : { stall: null, mutedChecks: mutedChecks + 1 };
};

// What barcode-detector throws when its reader is missing or broke while reading.
const READER_FAILED = /barcode detection service unavailable/i;

/**
 * The scanner's error, in the kinds the panel explains. Two failures the
 * library files as `unknown` get their own: the reader's WebAssembly that did
 * not load or broke, which a retry can fix, and a browser that answers the
 * camera with `NotSupportedError`, which it cannot.
 *
 * @param {{kind?: string, message?: string, cause?: {name?: string}}} error
 * @param {{readerMissing?: boolean}} [context]
 * @returns {{kind: string, message: string}}
 */
export const readCameraError = (error, { readerMissing = false } = {}) => {
  const message = error?.message || '';
  if (error?.kind && error.kind !== 'unknown') return { kind: error.kind, message };
  if (readerMissing || READER_FAILED.test(message)) return { kind: 'reader-unavailable', message };
  if (error?.cause?.name === 'NotSupportedError') return { kind: 'unsupported', message };
  return { kind: 'unknown', message };
};

const TYPE_THE_CODE = 'Mientras tanto, escribe el código abajo.';

const CAMERA_ERROR_COPY = {
  'permission-denied': {
    title: 'La cámara está bloqueada para este sitio',
    hint: `Permítela: en Chrome, toca el candado de la barra de direcciones → Permisos → Cámara; en Safari, «aA» → Configuración del sitio web → Cámara. ${TYPE_THE_CODE}`,
  },
  'no-camera': {
    title: 'Este equipo no tiene cámara',
    hint: 'Escribe el código abajo, o usa un lector conectado.',
  },
  'insecure-context': {
    title: 'La cámara necesita una conexión segura',
    hint: 'Abre el sitio con https, o escribe el código abajo.',
    canRetry: false,
  },
  'in-use': {
    title: 'Otra aplicación está usando la cámara',
    hint: 'Ciérrala y toca Reintentar.',
  },
  unsupported: {
    title: 'Este navegador no deja usar la cámara',
    hint: 'Ábrelo en Chrome o Safari, o escribe el código abajo.',
    canRetry: false,
  },
  security: {
    title: 'El navegador tiene la cámara desactivada',
    hint: `Actívala en su configuración y toca Reintentar. ${TYPE_THE_CODE}`,
  },
  'reader-unavailable': {
    title: 'No se pudo usar el lector de códigos',
    hint: `Revisa la conexión y toca Reintentar. ${TYPE_THE_CODE}`,
  },
  stopped: {
    title: 'La cámara se detuvo',
    hint: `Toca Reintentar. Si se repite, cierra las otras apps que usan la cámara. ${TYPE_THE_CODE}`,
  },
};

const UNKNOWN_CAMERA_ERROR = {
  title: 'La cámara no pudo abrir',
  hint: `Toca Reintentar. ${TYPE_THE_CODE}`,
};

/**
 * What to tell the operator when the camera fails, by the error's kind, and
 * whether retrying can help: a browser without camera access, or a page
 * without https, fails the same way every time.
 *
 * @param {string} kind
 * @returns {{title: string, hint: string, canRetry: boolean}}
 */
export const cameraErrorCopy = (kind) => ({ canRetry: true, ...(CAMERA_ERROR_COPY[kind] || UNKNOWN_CAMERA_ERROR) });
